import { PrizesInfo, PrizeTier } from 'types/types'
import { getOrdinalFromNumber } from 'types/utils'

type GameWithPrizes = {
  prize_tiers?: PrizeTier[] | null
  prizes?: PrizesInfo | null
}

const ordinal = (rank: number) => `${rank}${getOrdinalFromNumber(rank)}`

/** "1st", "4th–10th" */
export function formatRankRange(startRank: number, endRank: number): string {
  if (startRank === endRank) return ordinal(startRank)
  return `${ordinal(startRank)}–${ordinal(endRank)}`
}

/** What the tier pays, as shown to players: the admin's display name, else e.g. "£5.00 Site Credit". */
export function formatTierPrize(tier: PrizeTier): string {
  if (tier.display_name?.trim()) return tier.display_name.trim()
  if (tier.amount !== null && tier.amount !== undefined) {
    const label = tier.prize_type === 'CASH' ? 'Cash' : 'Site Credit'
    return `£${Number(tier.amount).toFixed(2)} ${label}`
  }
  return tier.description?.trim() || ''
}

function sortedTiers(game: GameWithPrizes): PrizeTier[] {
  return [...(game.prize_tiers ?? [])].sort((a, b) => a.start_rank - b.start_rank)
}

export function hasPrizeTiers(game: GameWithPrizes | null | undefined): boolean {
  return !!game?.prize_tiers?.length
}

/**
 * The game's prizes in the legacy { title, prizes: [{ label, value }] } shape, built from the
 * structured prize schedule when the game has one, so every prize card renders either kind.
 */
export function getGamePrizes(game: GameWithPrizes | null | undefined): PrizesInfo | null {
  if (!game) return null
  if (!hasPrizeTiers(game)) return game.prizes ?? null
  return {
    title: game.prizes?.title || 'Prizes',
    prizes: sortedTiers(game).map((tier) => ({
      label: `${formatRankRange(tier.start_rank, tier.end_rank)} place`,
      value: formatTierPrize(tier),
    })),
  }
}

/** The tier paying a leaderboard position. Tied players share a position, so they all match. */
export function getTierForPosition(game: GameWithPrizes, position: number): PrizeTier | null {
  return (
    sortedTiers(game).find((tier) => tier.start_rank <= position && position <= tier.end_rank) ??
    null
  )
}
